/*
 * Copyright (c) 2026 Bolmate B.V.
 */

import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import {initReactI18next} from 'react-i18next';
import {addCustomEventListener} from '@dvrd/dvr-controls';
import {DEFAULT_LANGUAGE, normalizeLanguage, SUPPORTED_LANGUAGES, UI_LANGUAGE_STORAGE_KEY} from './config';
import {applyRuntimeLocale} from './runtimeLocale';
import enCommon from './locales/en/common-en.json';
import nlCommon from './locales/nl/common-nl.json';

const isDev = process.env.NODE_ENV === 'development';

// Apply a best-effort runtime locale synchronously so that <html lang> and PrimeReact
// locale are set before first paint. The post-init .then() below confirms the final
// resolved language once the detector has run.
(() => {
    try {
        const stored = typeof window !== 'undefined' ? window.localStorage.getItem(UI_LANGUAGE_STORAGE_KEY) : null;
        applyRuntimeLocale(normalizeLanguage(stored ?? DEFAULT_LANGUAGE));
    } catch {
        applyRuntimeLocale(DEFAULT_LANGUAGE);
    }
})();

i18n.use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: {
            nl: {common: nlCommon},
            en: {common: enCommon}
        },
        defaultNS: 'common',
        fallbackLng: DEFAULT_LANGUAGE,
        supportedLngs: [...SUPPORTED_LANGUAGES],
        interpolation: {
            escapeValue: false
        },
        returnNull: false,
        returnEmptyString: false,
        saveMissing: isDev,
        missingKeyHandler: (lng, ns: string, key: string) => {
            if (!isDev) return;
            const list = Array.isArray(lng) ? lng.join(',') : String(lng);
            console.warn(`[i18n] Missing translation: [${list}] ${ns}:${key}`);
        },
        detection: {
            order: ['localStorage', 'htmlTag'],
            lookupLocalStorage: UI_LANGUAGE_STORAGE_KEY,
            caches: ['localStorage']
        }
    })
    .then(() => {
        applyRuntimeLocale(normalizeLanguage(i18n.resolvedLanguage ?? i18n.language));
    })
    .catch(err => {
        console.error('[i18n] init failed', err);
    });

i18n.on('languageChanged', (language: string) => {
    applyRuntimeLocale(language);
});

// @dvrd/dvr-controls' `showDialog` fills in Dutch defaults ("Bericht van het systeem", "Oke")
// before the event reaches DialogController. Register a listener here (before any component
// mount) that mutates the data in-place so the Dialog renders localized strings.
// Keep in sync with any new Dialog-producing features.
const DVR_DIALOG_DEFAULT_TITLE = 'Bericht van het systeem';
const DVR_DIALOG_DEFAULT_ACTION = 'Oke';
addCustomEventListener('bolmate-i18n-dialog-interceptor', 'onOpenDialog', (data: any) => {
    if (!data || typeof data !== 'object') return;
    if (data.title === DVR_DIALOG_DEFAULT_TITLE || data.title === undefined) {
        data.title = i18n.t('common.dialog.defaultTitle', DVR_DIALOG_DEFAULT_TITLE);
    }
    const actions = data.actions;
    const isDefaultActions =
        actions === undefined ||
        actions === null ||
        (Array.isArray(actions) && actions.length === 1 && actions[0] === DVR_DIALOG_DEFAULT_ACTION);
    if (isDefaultActions) {
        data.actions = [i18n.t('common.dialog.ok', DVR_DIALOG_DEFAULT_ACTION)];
    } else if (Array.isArray(actions)) {
        data.actions = actions.map((action: any) => {
            if (action === DVR_DIALOG_DEFAULT_ACTION) return i18n.t('common.dialog.ok', DVR_DIALOG_DEFAULT_ACTION);
            return action;
        });
    }
});

export default i18n;
